import { Injectable } from '@angular/core';
import { ApiService } from '@core/providers/http';
import { HttpParams } from '@angular/common/http';
import { Observable, map, forkJoin, of, catchError } from 'rxjs';
import {
  DashboardGeralDtoResponse,
  DashboardCampanhaEstadoDtoICollectionResponse,
  DashboardApoioEstadoDtoICollectionResponse,
  DashboardUtilizadorDtoResponse,
  DashboardConfiguracaoResumoDtoResponse,
  DashboardDetailDtoResponse
} from '../models/dashboard.model';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  constructor(private readonly apiService: ApiService) {}

  /**
   * Obtém totais consolidados de apoios confirmados (EstadoApoioENUM = 2).
   * Soma de valorDoadoTotal dos apoios confirmados para exibir valor único em todas as telas.
   */
  getTotalApoiosConfirmados(): Observable<{ totalValor: number; totalApoios: number }> {
    const params = new HttpParams({
      fromObject: {
        PageNumber: 1,
        PageSize: 2000,
        EstadoApoioENUM: 2,
      },
    });

    return this.apiService.get('/Apoio', params).pipe(
      map((res: any) => {
        const apoios: any[] = res?.data || [];
        const totalApoios = res?.totalRecords ?? apoios.length;

        const totalValor = apoios.reduce((acc: number, item: any) => {
          const valor = Number(item?.valorDoadoTotal ?? item?.valorApoio ?? 0) || 0;
          return acc + valor;
        }, 0);

        return {
          totalValor,
          totalApoios,
        };
      })
    );
  }

  /**
   * Obtém totais gerais do sistema (Campanhas, Apoios, Utilizadores, Configurações, Gestão de Páginas, Registos).
   */
  getDashboardGeral(): Observable<DashboardGeralDtoResponse> {
    return this.apiService.get('/Dashboard').pipe(
      map(response => response as DashboardGeralDtoResponse)
    );
  }

  /**
   * Obtém as métricas de campanhas agrupadas por estado.
   */
  getDashboardCampanhas(): Observable<DashboardCampanhaEstadoDtoICollectionResponse> {
    return this.apiService.get('/Dashboard/Campanhas').pipe(
      map(response => response as DashboardCampanhaEstadoDtoICollectionResponse)
    );
  }

  /**
   * Obtém as métricas de apoios/doações agrupadas por estado.
   */
  getDashboardApoios(): Observable<DashboardApoioEstadoDtoICollectionResponse> {
    return this.apiService.get('/Dashboard/Apoios').pipe(
      map(response => response as DashboardApoioEstadoDtoICollectionResponse)
    );
  }

  /**
   * Obtém o detalhe demográfico e quantitativo dos utilizadores da plataforma.
   */
  getDashboardUtilizadores(): Observable<DashboardUtilizadorDtoResponse> {
    return this.apiService.get('/Dashboard/Utilizadores').pipe(
      map(response => response as DashboardUtilizadorDtoResponse)
    );
  }

  /**
   * Obtém o resumo das configurações por tipo geral e gestão de páginas.
   */
  getDashboardConfiguracoes(): Observable<DashboardConfiguracaoResumoDtoResponse> {
    return this.apiService.get('/Dashboard/Configuracoes').pipe(
      map(response => response as DashboardConfiguracaoResumoDtoResponse)
    );
  }

  /**
   * Obtém o dashboard completo consolidado com todas as métricas detalhadas.
   */
  getDashboardCompleto(): Observable<DashboardDetailDtoResponse> {
    return this.apiService.get('/Dashboard/Completo').pipe(
      map(response => response as DashboardDetailDtoResponse)
    );
  }

  /**
   * Obtém as estatísticas para o público conforme regras de negócio:
   * - Campanhas: soma de activas, encerradas, sucesso e em curso
   * - Utilizadores: apenas utilizadores activos
   * - Pagamentos: apenas confirmados
   * - Taxa de sucesso: utilizadores activos com base nos pagamentos confirmados
   */
  getEstatisticasPublicas(): Observable<{
    totalCampanhas: number;
    totalCampanhasAtivas: number;
    totalCampanhasEncerradas: number;
    totalCampanhasSucesso: number;
    totalCampanhasEmCurso: number;
    totalUtilizadoresAtivos: number;
    pagamentosConfirmados: number;
    totalFinanciado: number;
    taxaSucesso: number;
  }> {
    return forkJoin({
      dashboard: this.getDashboardCompleto().pipe(catchError(() => of(null))),
      apoiosConfirmados: this.getTotalApoiosConfirmados().pipe(
        catchError(() => of({ totalValor: 0, totalApoios: 0 }))
      ),
    }).pipe(
      map(({ dashboard, apoiosConfirmados }) => {
        const data = (dashboard as any)?.data;
        const campanhasEstados = data?.campanhasPorEstado || [];
        const apoiosEstados = data?.apoiosPorEstado || [];
        const utilizadores = data?.utilizadores;

        // 1. Campanhas: somar activas, encerradas, sucesso e em curso
        let totalCampanhas = 0;
        let totalCampanhasAtivas = 0;
        let totalCampanhasEncerradas = 0;
        let totalCampanhasSucesso = 0;
        let totalCampanhasEmCurso = 0;

        if (Array.isArray(campanhasEstados)) {
          campanhasEstados.forEach((item: any) => {
            const estado = Number(item.estadoCampanhaENUM);
            const desc = (item.estadoCampanha || '')
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '');

            const isEmCurso = estado === 4 || desc.includes('curso') || desc.includes('andamento');
            const isAtiva = estado === 3 || desc.includes('ativ') || desc.includes('publicad');
            const isEncerrada = estado === 8 || desc.includes('encerrad');
            const isSucesso = estado === 6 || estado === 9 || desc.includes('sucesso') || desc.includes('concluid');

            if (isEmCurso) totalCampanhasEmCurso += Number(item.total) || 0;
            if (isAtiva) totalCampanhasAtivas += Number(item.total) || 0;
            if (isEncerrada) totalCampanhasEncerradas += Number(item.total) || 0;
            if (isSucesso) totalCampanhasSucesso += Number(item.total) || 0;

            if (isEmCurso || isAtiva || isEncerrada || isSucesso) {
              totalCampanhas += Number(item.total) || 0;
            }
          });
        }

        // 2. Utilizadores: apenas os activos
        const totalUtilizadoresAtivos = Number(utilizadores?.ativos) || 0;

        // 3. Pagamentos: apenas os confirmados
        let pagamentosConfirmados = apoiosConfirmados?.totalApoios || 0;
        if (!pagamentosConfirmados && Array.isArray(apoiosEstados)) {
          const itemConfirmado = apoiosEstados.find(
            (item: any) =>
              Number(item.estadoApoioENUM) === 2 ||
              (item.estadoApoio || '').toLowerCase().includes('confirmad')
          );
          if (itemConfirmado) {
            pagamentosConfirmados = Number(itemConfirmado.total) || 0;
          }
        }

        // 4. Total financiado / valor consolidado em Kz (soma de valorDoadoTotal)
        const totalFinanciado = apoiosConfirmados?.totalValor || 0;

        // 5. Taxa de sucesso: utilizadores activos com base nos pagamentos confirmados
        let taxaSucesso = 0;
        if (totalUtilizadoresAtivos > 0 && pagamentosConfirmados > 0) {
          taxaSucesso = Math.min(
            100,
            Math.round((pagamentosConfirmados / totalUtilizadoresAtivos) * 100)
          );
        }

        return {
          totalCampanhas,
          totalCampanhasAtivas,
          totalCampanhasEncerradas,
          totalCampanhasSucesso,
          totalCampanhasEmCurso,
          totalUtilizadoresAtivos,
          pagamentosConfirmados,
          totalFinanciado,
          taxaSucesso
        };
      })
    );
  }
}
